/* First-slide markup stays visible before this deferred enhancement runs. */
(() => {
  if (document.documentElement.dataset.arcStoryController === 'true') return;
  document.documentElement.dataset.arcStoryController = 'true';
function initialize(root) {
  if (!root || root.dataset.arcStoryReady === 'true') return;
  root.dataset.arcStoryReady = 'true';

  const slides = Array.from(root.querySelectorAll('.arc-story__slide'));
  if (!slides.length) return;
  const dots = Array.from(root.querySelectorAll('.arc-story__dot'));
  const stage = root.querySelector('.arc-story__stage');
  const rotationButton = root.querySelector('.arc-story__rotation');
  const pauseIcon = root.querySelector('.arc-story__pause-icon');
  const playIcon = root.querySelector('.arc-story__play-icon');
  const progress = root.querySelector('.arc-story__progress-value');
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const autoplaySpeed = Math.max(1000, Number(root.dataset.autoplaySpeed) || 8000);
  const mediaLoads = new WeakMap();
  const events = new AbortController();
  const progressAnimation = progress?.animate([
    { strokeDashoffset: '100' }, { strokeDashoffset: '0' }
  ], { duration: autoplaySpeed, fill: 'both', easing: 'linear' });
  if (progressAnimation) {
    progressAnimation.pause();
    progressAnimation.currentTime = 0;
  }

  let current = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
  let requested = current;
  let requestId = 0;
  let loading = false;
  let pendingAutomatic = false;
  let autoplayTimer;
  let preloadTimer;
  let heatTimer;
  let heatFrame;
  let heatScheduled = false;
  let elapsed = 0;
  let startedAt = null;
  let baseReady = false;
  let inView = !('IntersectionObserver' in window);
  let disposed = false;
  let touchOrigin = null;
  let userPaused = false;
  let hoverPaused = false;
  let hoverOverride = false;
  let rotationClickPaused = null;

  function waitForImage(img) {
    if (!img || disposed) return Promise.resolve();
    return new Promise((resolve) => {
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        window.clearTimeout(timeout);
        img.removeEventListener('load', loaded);
        img.removeEventListener('error', finish);
        events.signal.removeEventListener('abort', finish);
        resolve();
      };
      const loaded = () => {
        if (img.decode) img.decode().catch(() => {}).then(finish);
        else finish();
      };
      const timeout = window.setTimeout(finish, 12000);
      events.signal.addEventListener('abort', finish, { once: true });
      if (img.complete) loaded();
      else {
        img.addEventListener('load', loaded, { once: true });
        img.addEventListener('error', finish, { once: true });
      }
    });
  }

  function loadMedia(slide) {
    if (!slide || disposed) return Promise.resolve();
    if (mediaLoads.has(slide)) return mediaLoads.get(slide);
    const template = slide.querySelector('template[data-arc-slide-media]');
    if (template) template.replaceWith(template.content.cloneNode(true));
    const ready = waitForImage(slide.querySelector('.arc-story__standard-image'));
    mediaLoads.set(slide, ready);
    return ready;
  }

  function pausedByControl() {
    return userPaused || (hoverPaused && !hoverOverride);
  }

  function canRotate() {
    return !disposed && inView && !document.hidden && !motion.matches &&
      !pausedByControl() && !touchOrigin && slides.length > 1 && root.dataset.autoplay === 'true';
  }

  function stopClock() {
    if (startedAt !== null) elapsed = Math.min(autoplaySpeed, elapsed + performance.now() - startedAt);
    startedAt = null;
    window.clearTimeout(autoplayTimer);
    window.clearTimeout(preloadTimer);
    if (progressAnimation) {
      progressAnimation.pause();
      progressAnimation.currentTime = elapsed;
    }
  }

  function startClock() {
    if (startedAt !== null || !baseReady || loading || !canRotate()) return;
    startedAt = performance.now();
    if (progressAnimation) {
      progressAnimation.currentTime = elapsed;
      if (elapsed < autoplaySpeed) progressAnimation.play();
    }
    autoplayTimer = window.setTimeout(() => {
      stopClock();
      elapsed = autoplaySpeed;
      activateSlide((current + 1) % slides.length, true);
    }, Math.max(0, autoplaySpeed - elapsed));
    preloadTimer = window.setTimeout(() => {
      if (canRotate() && !loading) loadMedia(slides[(current + 1) % slides.length]);
    }, Math.max(0, autoplaySpeed * 0.65 - elapsed));
  }

  function scheduleHeat() {
    if (!baseReady || !canRotate() || current !== 0 || heatScheduled ||
        !root.querySelector('template[data-arc-heat-media]')) return;
    heatScheduled = true;
    heatFrame = requestAnimationFrame(() => {
      heatFrame = requestAnimationFrame(() => {
        const insert = () => {
          heatScheduled = false;
          if (!canRotate() || current !== 0) return;
          const template = root.querySelector('template[data-arc-heat-media]');
          if (template) template.replaceWith(template.content.cloneNode(true));
        };
        if ('requestIdleCallback' in window) heatTimer = window.requestIdleCallback(insert, { timeout: 2000 });
        else heatTimer = window.setTimeout(insert, 200);
      });
    });
  }

  function refreshPlayback() {
    if (disposed) return;
    if (pendingAutomatic && !canRotate()) {
      requestId += 1;
      requested = current;
      loading = false;
      pendingAutomatic = false;
    }
    const paused = !canRotate();
    if (rotationButton) {
      const controlPaused = pausedByControl();
      const label = controlPaused ? rotationButton.dataset.playLabel : rotationButton.dataset.pauseLabel;
      rotationButton.hidden = motion.matches || slides.length < 2 || root.dataset.autoplay !== 'true';
      rotationButton.setAttribute('aria-label', label);
      rotationButton.setAttribute('title', label);
      pauseIcon?.toggleAttribute('hidden', controlPaused);
      playIcon?.toggleAttribute('hidden', !controlPaused);
    }
    root.dataset.arcPaused = String(paused);
    if (paused) slides[current].classList.add('is-copy-settled');
    if (stage) stage.setAttribute('aria-live', paused ? 'polite' : 'off');
    if (paused || loading || !baseReady) stopClock();
    else startClock();
    scheduleHeat();
  }

  async function activateSlide(index, automatic = false) {
    if (disposed || !slides[index] || (automatic && !canRotate())) return;
    const token = ++requestId;
    requested = index;
    stopClock();
    loading = index !== current;
    pendingAutomatic = automatic && loading;
    if (!loading) {
      refreshPlayback();
      return;
    }
    await loadMedia(slides[index]);
    if (disposed || token !== requestId) return;
    loading = false;
    pendingAutomatic = false;
    if (automatic && !canRotate()) {
      requested = current;
      refreshPlayback();
      return;
    }

    const oldSlide = slides[current];
    const newSlide = slides[index];
    oldSlide.classList.remove('is-active');
    oldSlide.classList.add('is-leaving-left');
    oldSlide.setAttribute('aria-hidden', 'true');
    oldSlide.inert = true;
    if (motion.matches) oldSlide.classList.remove('is-leaving-left');
    newSlide.classList.remove('is-leaving-left');
    newSlide.classList.toggle('is-copy-settled', !canRotate());
    newSlide.classList.add('is-active');
    newSlide.setAttribute('aria-hidden', 'false');
    newSlide.inert = false;
    dots.forEach((dot, dotIndex) => {
      const active = dotIndex === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', String(active));
    });
    current = index;
    elapsed = 0;
    if (progressAnimation) progressAnimation.currentTime = 0;
    refreshPlayback();
  }

  const listen = (target, type, callback, options = {}) => {
    if (target) target.addEventListener(type, callback, { ...options, signal: events.signal });
  };
  listen(root, 'transitionend', (event) => {
    if (event.propertyName !== 'opacity' || !slides.includes(event.target)) return;
    if (!event.target.classList.contains('is-active')) event.target.classList.remove('is-leaving-left');
  });
  listen(rotationButton, 'pointerdown', () => { rotationClickPaused = pausedByControl(); });
  listen(rotationButton, 'pointercancel', () => { rotationClickPaused = null; });
  listen(rotationButton, 'blur', () => { rotationClickPaused = null; });
  listen(rotationButton, 'click', () => {
    const resume = rotationClickPaused ?? pausedByControl();
    rotationClickPaused = null;
    userPaused = !resume;
    hoverOverride = resume;
    refreshPlayback();
  });
  listen(root, 'focusin', () => {
    userPaused = true;
    refreshPlayback();
  });
  listen(root, 'pointerenter', (event) => {
    if (event.pointerType !== 'mouse') return;
    hoverPaused = true;
    hoverOverride = false;
    refreshPlayback();
  });
  listen(root, 'pointerleave', (event) => {
    if (event.pointerType !== 'mouse') return;
    hoverPaused = false;
    hoverOverride = false;
    refreshPlayback();
  });
  const next = () => activateSlide((requested + 1) % slides.length);
  const previous = () => activateSlide((requested - 1 + slides.length) % slides.length);
  dots.forEach((dot) => listen(dot, 'click', () => activateSlide(Number(dot.dataset.goTo))));
  listen(root.querySelector('.arc-story__next'), 'click', next);
  listen(root.querySelector('.arc-story__prev'), 'click', previous);
  listen(root, 'touchstart', (event) => {
    const touch = event.changedTouches[0];
    if (!touch) return;
    touchOrigin = { x: touch.screenX, y: touch.screenY };
    refreshPlayback();
  }, { passive: true });
  listen(root, 'touchend', (event) => {
    const touch = event.changedTouches[0];
    const origin = touchOrigin;
    touchOrigin = null;
    if (touch && origin) {
      const dx = touch.screenX - origin.x;
      const dy = touch.screenY - origin.y;
      if (Math.abs(dx) >= 48 && Math.abs(dx) > Math.abs(dy)) {
        userPaused = true;
        dx < 0 ? next() : previous();
      }
    }
    refreshPlayback();
  }, { passive: true });
  listen(root, 'touchcancel', () => { touchOrigin = null; refreshPlayback(); }, { passive: true });
  listen(document, 'visibilitychange', refreshPlayback);
  listen(motion, 'change', refreshPlayback);
  listen(document, 'shopify:block:select', (event) => {
    const slide = slides.find((item) => item.dataset.blockId === event.detail.blockId);
    if (slide) {
      userPaused = true;
      activateSlide(Number(slide.dataset.slideIndex));
    }
  });

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    inView = entries[0].isIntersecting;
    refreshPlayback();
  }) : null;
  if (observer) observer.observe(root);
  listen(document, 'shopify:section:unload', (event) => {
    if (event.detail.sectionId !== root.id.slice('ArcStory-'.length)) return;
    disposed = true;
    requestId += 1;
    stopClock();
    progressAnimation?.cancel();
    cancelAnimationFrame(heatFrame);
    if ('cancelIdleCallback' in window) window.cancelIdleCallback(heatTimer);
    window.clearTimeout(heatTimer);
    if (observer) observer.disconnect();
    events.abort();
  });

  refreshPlayback();
  waitForImage(root.querySelector('.arc-story__image--base')).then(() => {
    baseReady = true;
    refreshPlayback();
  });
}
  function initializeWithin(container) {
    if (container.matches?.('.arc-story')) initialize(container);
    container.querySelectorAll('.arc-story').forEach(initialize);
  }
  initializeWithin(document);
  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
})();
